/** Phase 6.3: scene art (Grok Imagine): the Images tab, the "the DM shows you…" popup and wiki portraits. */
import { useEffect, useState } from "react";
import { IMAGE_KINDS, type CampaignImage, type ImageKind, type ImagesResponse, type WikiIndexEntry } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

const KIND_LABEL: Record<ImageKind, string> = { scene: "Scene", portrait: "Portrait", map: "Map" };
const money = (n: number) => `$${n < 0.1 ? n.toFixed(3).replace(/0$/, "") : n.toFixed(2)}`;

export function ImagesPage() {
  const { campaign, isOwner, imageVersion, fail } = useGame();
  const [data, setData] = useState<ImagesResponse | null>(null);
  const [entries, setEntries] = useState<WikiIndexEntry[]>([]);
  const [kind, setKind] = useState<ImageKind>("scene");
  const [prompt, setPrompt] = useState("");
  const [caption, setCaption] = useState("");
  const [memoryId, setMemoryId] = useState<number | "">("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState<CampaignImage | null>(null);
  const load = () => api.images(campaign.id).then(setData, fail);
  useEffect(() => void load(), [campaign.id, imageVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data?.canCreate) api.wiki(campaign.id).then((w) => setEntries(w.entries.filter((e) => e.kind !== "quest")), () => undefined);
  }, [campaign.id, data?.canCreate]);
  if (!data) return <main className="muted">Loading…</main>;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const make = () => run(async () => {
    await api.createImage(campaign.id, { kind, prompt, caption, memoryId: memoryId === "" ? null : memoryId, show });
    setPrompt("");
    setCaption("");
  });
  return (
    <main className="images-page">
      {data.canCreate && (
        <section className="panel">
          <h3>Make an image</h3>
          <p className="muted small">
            Grok Imagine paints it in about 10 seconds, on this campaign's xAI key:{" "}
            {data.pricePerImage === null ? <strong>no price set for {data.model} (Admin → Prices)</strong> : <strong>{money(data.pricePerImage)} per image</strong>}
            {" "}(shows up on the Costs page). Players only see images you show.
          </p>
          <div className="row">
            <label>Kind{" "}
              <select value={kind} onChange={(e) => setKind(e.target.value as ImageKind)}>
                {IMAGE_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </label>
            <label>Link to{" "}
              <select value={memoryId} onChange={(e) => {
                const v = e.target.value ? Number(e.target.value) : "";
                setMemoryId(v);
                const m = entries.find((x) => x.id === v);
                if (m && !caption) setCaption(m.title);
              }}>
                <option value="">(nothing)</option>
                {entries.map((e) => <option key={e.id} value={e.id}>{e.title} ({e.kind})</option>)}
              </select>
            </label>
          </div>
          <textarea rows={3} maxLength={900} placeholder={kind === "portrait" ? "e.g. Old Marta, a weathered ferrywoman with a lantern and a sly grin" : kind === "map" ? "e.g. A small fishing village on a cliff, a bell tower in the bay" : "e.g. A drowned bell tower rising from a misty bay at dawn"}
            value={prompt} onChange={(e) => setPrompt(e.target.value)} aria-label="Describe the picture" />
          <div className="row">
            <input placeholder="Caption players see (optional)" maxLength={200} value={caption} onChange={(e) => setCaption(e.target.value)} />
            <label><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show to the table right away</label>
            <button className="primary" disabled={busy || prompt.trim().length < 3} onClick={() => void make()}>{busy ? "Painting…" : "Make image"}</button>
          </div>
          <details className="dm-images" open={data.dmImages}>
            <summary>Let the DM show pictures by itself {data.dmImages ? "(on)" : "(off)"}</summary>
            <p className="muted small">
              When on, the DM may show scene art or a portrait at big moments, up to the limit per session ({data.dmImagesThisSession} used this session).
              Each one costs the per-image price on your key.
            </p>
            {isOwner ? (
              <div className="row">
                <label><input type="checkbox" checked={data.dmImages} disabled={busy}
                  onChange={(e) => void run(() => api.imageSettings(campaign.id, { dmImages: e.target.checked }))} /> DM may show pictures</label>
                <label>Per session{" "}
                  <input type="number" min={0} max={30} style={{ width: "4em" }} defaultValue={data.dmImagesPerSession} disabled={busy}
                    onBlur={(e) => Number(e.target.value) !== data.dmImagesPerSession && void run(() => api.imageSettings(campaign.id, { dmImagesPerSession: Number(e.target.value) }))} />
                </label>
              </div>
            ) : <p className="muted small">Only the campaign's Host can change this.</p>}
          </details>
        </section>
      )}
      <section className="panel">
        <h3>Images</h3>
        {!data.images.length && <p className="muted">No pictures yet.{data.canCreate ? "" : " The Host or the DM shows them here."}</p>}
        <div className="gallery">
          {data.images.map((im) => (
            <figure key={im.id} className={im.shown ? "" : "hidden-img"}>
              <img src={im.url} alt={im.caption || KIND_LABEL[im.kind]} loading="lazy" onClick={() => setBig(im)} />
              <figcaption>
                <strong>{im.caption || KIND_LABEL[im.kind]}</strong>
                {im.memoryTitle && <> · <a href={`#/wiki/${im.memoryId}`}>{im.memoryTitle}</a></>}
                <span className="muted small"> · {KIND_LABEL[im.kind]}{im.byDm ? " by the DM" : ""}</span>
                {data.canCreate && (
                  <div className="row small">
                    {!im.shown && <span className="tag">hidden</span>}
                    <button disabled={busy} onClick={() => void run(() => api.updateImage(campaign.id, im.id, { shown: !im.shown }))}>{im.shown ? "Hide" : "Show to the table"}</button>
                    <button className="link danger" disabled={busy} onClick={() => window.confirm("Delete this image?") && void run(() => api.deleteImage(campaign.id, im.id))}>Delete</button>
                  </div>
                )}
                {data.canCreate && im.prompt && <div className="muted small" title="What was asked for (players don't see this)">{im.prompt}</div>}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
      {big && <ImageModal image={big} onClose={() => setBig(null)} />}
    </main>
  );
}

function ImageModal({ image, onClose, title }: { image: CampaignImage; onClose: () => void; title?: string }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="image-modal" onClick={onClose} role="dialog" aria-label={image.caption || "Image"}>
      <figure onClick={(e) => e.stopPropagation()}>
        {title && <div className="image-modal-title">{title}</div>}
        <img src={image.url} alt={image.caption || "Image"} />
        <figcaption>
          {image.caption}
          <button onClick={onClose}>Close</button>
        </figcaption>
      </figure>
    </div>
  );
}

/** Pops up whenever the Host or the DM shows a picture. */
export function ImagePopup() {
  const { popupImage, clearPopupImage } = useGame();
  if (!popupImage) return null;
  return <ImageModal image={popupImage} onClose={clearPopupImage} title={popupImage.byDm ? "The DM shows you…" : "The Host shows you…"} />;
}

/** Portraits / pictures linked to a wiki entry (players: shown ones only). */
export function WikiPictures({ memoryId }: { memoryId: number }) {
  const { campaign, imageVersion, isHost, fail } = useGame();
  const [list, setList] = useState<CampaignImage[]>([]);
  const [big, setBig] = useState<CampaignImage | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [v, setV] = useState(0);
  useEffect(() => {
    api.images(campaign.id).then((r) => {
      const mine = r.images.filter((i) => i.memoryId === memoryId);
      // Main picture first, then newest.
      setList([...mine.filter((i) => i.main), ...mine.filter((i) => !i.main)]);
    }, () => setList([]));
  }, [campaign.id, memoryId, imageVersion, v]);
  const act = (p: Promise<unknown>) => { setBusy(true); p.then(() => setV((x) => x + 1), fail).finally(() => setBusy(false)); };
  if (!list.length && !isHost) return null;
  return (
    <div className="wiki-pictures">
      {list.slice(0, 4).map((im) => (
        <span key={im.id} className="wiki-pic">
          <img src={im.url} alt={im.caption || "Picture"} title={im.caption + (im.main ? " (main picture)" : "")} onClick={() => setBig(im)} className={(im.shown ? "" : "hidden-img") + (im.main && list.length > 1 ? " main-img" : "")} />
          {isHost && !im.main && list.length > 1 && (
            <button className="link small" disabled={busy} onClick={() => act(api.setMainImage(campaign.id, im.id))} title="Use this as the main picture (and the reference for new ones)">Make main</button>
          )}
        </span>
      ))}
      {isHost && (
        <form className="wiki-new-pic" onSubmit={(e) => { e.preventDefault(); act(api.newEntryPicture(campaign.id, memoryId, { show: false, note: note.trim() || undefined }).then(() => setNote(""))); }}>
          <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} disabled={busy}
            placeholder={list.length ? "Optional note, e.g. at night, in armour" : "Optional note for the picture"} aria-label="Note for the new picture" />
          <button type="submit" className="small" disabled={busy}
            title={list.length ? "Paint a new picture, using the main one as a reference so they look alike" : "Paint a picture of this entry"}>
            {busy ? "Painting…" : "New picture"}
          </button>
        </form>
      )}
      {big && <ImageModal image={big} onClose={() => setBig(null)} />}
    </div>
  );
}
